/**
 * CatalogContext — loads the public catalog once at app boot and exposes it
 * to every consumer via `useCatalog()`. The first paint always renders with
 * the bundled static catalog (no skeletons, no flicker) and is silently
 * replaced if/when the API responds with non-empty data.
 *
 * Consumers should read `categories`, `treatments`, and helper functions —
 * never import the static module directly from a page.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  fetchCategories,
  fetchTreatments,
  fetchOverrides,
  STATIC_CATALOG,
  mergeTreatment,
  isApiConfigured,
} from '../lib/catalogSource';
import { getCategoryCardImage } from '../lib/imageOverrides';

const CatalogContext = createContext({
  categories: STATIC_CATALOG.categories,
  treatments: STATIC_CATALOG.treatments,
  overrides: {},
  source: 'static',
  loading: false,
  apiEnabled: false,
});

export const CatalogProvider = ({ children }) => {
  const [state, setState] = useState({
    categories: STATIC_CATALOG.categories,
    treatments: STATIC_CATALOG.treatments,
    overrides: {},
    source: 'static',
    loading: isApiConfigured(),
    apiEnabled: isApiConfigured(),
  });

  useEffect(() => {
    if (!isApiConfigured()) return undefined;
    let cancelled = false;

    (async () => {
      const [cat, treat, ov] = await Promise.all([
        fetchCategories(),
        fetchTreatments(),
        fetchOverrides(),
      ]);
      if (cancelled) return;

      const apiOk = cat.source === 'api' && treat.source === 'api';

      setState({
        categories: cat.categories,
        treatments: treat.treatments,
        overrides: ov.overrides || {},
        source: apiOk ? 'api' : 'static',
        loading: false,
        apiEnabled: true,
      });

      // Only log in development so we don't spam production consoles.
      if (process.env.NODE_ENV !== 'production') {
        const liveOv = ov.overrides || {};
        const treatmentOverrideSlugs = Object.keys(liveOv.treatments || {});
        // eslint-disable-next-line no-console
        console.info('[Catalog]', {
          categories: cat.source,
          treatments: treat.source,
          treatmentCount: treat.treatments.length,
          categoryReason: cat.reason,
          treatmentReason: treat.reason,
          overrideTopLevelKeys: Object.keys(liveOv),
          treatmentOverrideSlugs,
          treatmentOverrideCount: treatmentOverrideSlugs.length,
          apiBase: process.env.REACT_APP_API_URL || '(unset)',
        });
        // Live inspector — open DevTools and type `__vcOverrides` to see the
        // exact blob driving the UI. Strictly dev-only.
        if (typeof window !== 'undefined') {
          // eslint-disable-next-line no-underscore-dangle
          window.__vcOverrides = liveOv;
          // eslint-disable-next-line no-underscore-dangle
          window.__vcCatalog = {
            categories: cat.categories,
            treatments: treat.treatments,
            apiBase: process.env.REACT_APP_API_URL || '(unset)',
          };
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => {
    const treatmentsBySlug = new Map(state.treatments.map((t) => [t.slug, t]));
    const staticBySlug = new Map(STATIC_CATALOG.treatments.map((t) => [t.slug, t]));

    const resolveTreatment = (slug) => {
      if (!slug) return null;
      const apiEntry = treatmentsBySlug.get(slug) || null;
      const staticEntry = staticBySlug.get(slug) || null;
      if (!apiEntry && !staticEntry) return null;
      return mergeTreatment(apiEntry, staticEntry, state.overrides);
    };

    // Pre-merged list used for listing pages / popular grids — every entry
    // already has admin-override image + long-form supplement applied, so
    // consumers just read `t.image` / `t.shortDesc` without extra wiring.
    const treatmentsDisplay = state.treatments
      .map((t) => resolveTreatment(t.slug))
      .filter(Boolean);

    return {
      ...state,
      treatmentsDisplay,
      // Resolves an entry the way the detail page wants it:
      //   API entry (if any) ← supplemented with static long-form ← admin override.
      getTreatmentBySlug: resolveTreatment,
      getCategoryBySlug: (slug) =>
        state.categories.find((c) => c.slug === slug) || null,
      getTreatmentsByCategory: (categorySlug) =>
        treatmentsDisplay.filter((t) => t.category === categorySlug),
      // Resolves the final hero image for a category card (admin override → static)
      getCategoryImage: (slug, fallback) =>
        getCategoryCardImage(state.overrides, slug, fallback),
    };
  }, [state]);

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
};

export const useCatalog = () => useContext(CatalogContext);
